import { useEffect, useState } from 'react';
import { fetchAllAirtableRecords } from '../services/airtable';
import { normalizeEmail } from '../utils/text';

const normalizeService = (service = '', obligacion = '') => {
  const key = (service || obligacion || '').toUpperCase();
  if (key.includes('NOM')) return 'NOM';
  if (key.includes('SUA')) return 'SUA';
  if (key.includes('ISN')) return 'ISN/ISH';
  if (key.includes('DIOT')) return 'DIOT';
  if (key.includes('CE')) return 'CE';
  if (key === 'OTRO' || key.includes('OTRO')) return 'Otro';
  if (key.includes('SAT')) return 'SAT';
  return 'SAT';
};

const parseDate = value => {
  if (!value) return null;
  const iso = String(value).slice(0, 10);
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const computeStatus = (estado, fechaProgramada, fechaRealizada, today) => {
  if (estado && estado !== 'pending') return estado;
  if (fechaRealizada) return 'done';
  if (!fechaProgramada) return estado || 'pending';
  const diffDays = Math.floor((fechaProgramada.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'late';
  if (diffDays <= 3) return 'dueSoon';
  return 'ontrack';
};

const NOTES_FIELD_ID = 'fldILbsanV5Bj3JHJ';
const CALENDARIO_ENLACE_FIELD_ID = 'fldBNUzQM51qz2yFR';
const ARCHIVOS_TABLE_ID = 'tblOTX32oPFJfpNZx';
const ARCHIVOS_FIELD_IDS = {
  url: 'fldU7lyhG0HKXV3BY',
  calendarioLink: 'fldIOVE8g0ziUfHJT',
  fechaCarga: 'fld5V7U7HTVdTwYhe'
};

/**
 * Retorna matriz de actividades mensuales (agrupadas por periodo y cliente).
 */
export function useCalendarioActivities({
  enabled,
  calendarioTableId,
  clientDirectory,
  userDirectory,
  refreshIndex = 0
}) {
  const [activitySummaryData, setActivitySummaryData] = useState({});
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activitiesError, setActivitiesError] = useState('');

  useEffect(() => {
    if (!enabled || !calendarioTableId) return;
    let ignore = false;
    async function loadCalendarActivities() {
      try {
        setLoadingActivities(true);
        setActivitiesError('');
        const uploadFlagField = 'Subir Archivo';
        const [records, archivosRecords] = await Promise.all([
          fetchAllAirtableRecords({
            tableId: calendarioTableId,
            maxRecords: 1000
          }),
          fetchAllAirtableRecords({
            tableId: ARCHIVOS_TABLE_ID,
            maxRecords: 1000,
            returnFieldsByFieldId: true,
            fields: [
              ARCHIVOS_FIELD_IDS.url,
              ARCHIVOS_FIELD_IDS.calendarioLink,
              ARCHIVOS_FIELD_IDS.fechaCarga
            ]
          }).catch(error => {
            console.warn('[Airtable] No se pudieron cargar los Archivos:', error);
            return [];
          })
        ]);
        if (ignore) return;
        const matrix = {};
        const now = new Date();
        const fallbackPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

        const archivosByCalendarId = new Map();
        const getArchivosTimestamp = (fields, createdTime) => {
          const fechaCarga = parseDate(fields?.[ARCHIVOS_FIELD_IDS.fechaCarga]);
          if (fechaCarga) return fechaCarga.getTime();
          if (!createdTime) return 0;
          const created = new Date(createdTime);
          return Number.isNaN(created.getTime()) ? 0 : created.getTime();
        };

        (archivosRecords || []).forEach(record => {
          const fields = record.fields || {};
          const url = fields[ARCHIVOS_FIELD_IDS.url] || '';
          const linked = fields[ARCHIVOS_FIELD_IDS.calendarioLink];
          if (!url || !Array.isArray(linked) || !linked.length) return;
          const timestamp = getArchivosTimestamp(fields, record.createdTime);
          linked.forEach(calendarId => {
            const existing = archivosByCalendarId.get(calendarId);
            if (!existing || timestamp > existing.timestamp) {
              archivosByCalendarId.set(calendarId, { url, timestamp });
            }
          });
        });

        const ensureRow = (monthKey, clientId) => {
          if (!matrix[monthKey]) matrix[monthKey] = [];
          let row = matrix[monthKey].find(item => item.clientId === clientId);
          if (!row) {
            const clientInfo = clientDirectory[clientId] || {};
            const listaCtesIdCliente = clientInfo.idCliente || '';
            const listaCtesEmpresa = clientInfo.empresa || clientInfo.name || clientId;
            row = {
              clientId,
              client: clientInfo.name || clientId,
              shortName: clientInfo.name || clientId,
              clientInfo,
              listaCtesIdCliente,
              listaCtesEmpresa,
              listaCtesAlias: clientInfo.alias || '',
              listaCtesNombre: clientInfo.nombreComercial || clientInfo.name || '',
              responsables: [],
              responsablesRaw: [],
              responsableEmails: [],
              obligations: {}
            };
            matrix[monthKey].push(row);
          }
          return row;
        };
        const appendUnique = (target, values) => {
          const list = Array.isArray(values) ? values : [];
          if (!list.length) return;
          list.forEach(value => {
            if (!value) return;
            if (!target.includes(value)) {
              target.push(value);
            }
          });
        };
        const parseResponsables = value => {
          const rawList = Array.isArray(value)
            ? value.slice()
            : typeof value === 'string' && value.trim()
              ? value.split(',').map(item => item.trim()).filter(Boolean)
              : [];
          const responsables = rawList.map(id => userDirectory[id]?.iniciales || id);
          const responsableEmails = rawList
            .map(id => userDirectory[id]?.correo || (String(id).includes('@') ? id : ''))
            .filter(Boolean)
            .map(normalizeEmail);
          return {
            responsablesRaw: rawList,
            responsables,
            responsableEmails
          };
        };

        records.forEach(record => {
          const fields = record.fields || {};
          const rawClient = fields.Cliente;
          const clientId = Array.isArray(rawClient) ? rawClient[0] : rawClient;
          if (!clientId) return;
          const periodMatch = (fields.Actividad || '').match(/(20\d{2}-\d{2})/);
          const period = periodMatch ? periodMatch[1] : (fields['Fecha programada'] || '').slice(0, 7) || fallbackPeriod;
          const row = ensureRow(period, clientId);
          const rawService =
            fields['Servicio/Proceso'] ||
            fields.fld9n9lkA4uUhXxBw ||
            fields['service/proceso'] ||
            fields.servicio ||
            fields.proceso ||
            '';
          const serviceKey = normalizeService(rawService, fields.Obligacion);
          const fechaProg = parseDate(fields['Fecha programada']);
          const fechaReal = parseDate(fields['Fecha realizada']);
          const status = computeStatus(fields['Estado semáforo'], fechaProg, fechaReal, now);
          const dia = fields['Día objetivo'] || '';
          const dueText = fields['Fecha programada'] || dia || '';
          const requiresUploadRaw = fields[uploadFlagField];
          const requiresUploadId = fields.fldGo1yOHU1aHqOZn;
          const normalizeFlag = value => {
            if (value === undefined || value === null) return false;
            if (typeof value === 'boolean') return value;
            const str = String(value).toLowerCase();
            return ['true', '1', 'checked', 'yes', 'si'].includes(str);
          };
          const requiresUpload = normalizeFlag(requiresUploadRaw) || normalizeFlag(requiresUploadId);
          const notes = fields['Notas internas'] ?? fields[NOTES_FIELD_ID] ?? '';
          const enlaceLookup = fields[CALENDARIO_ENLACE_FIELD_ID];
          const driveUrl =
            (Array.isArray(enlaceLookup) && enlaceLookup[0]) ||
            archivosByCalendarId.get(record.id)?.url ||
            '';
          const responsablesValue = fields.Responsable;
          const {
            responsablesRaw,
            responsables,
            responsableEmails
          } = parseResponsables(responsablesValue);
          row.obligations[serviceKey] = {
            status,
            activity: fields.Actividad || serviceKey,
            dia,
            dueText,
            requiresUpload,
            recordId: record.id,
            notes,
            driveUrl,
            responsables,
            responsablesRaw,
            responsableEmails
          };
          appendUnique(row.responsablesRaw, responsablesRaw);
          appendUnique(row.responsables, responsables);
          appendUnique(row.responsableEmails, responsableEmails);
        });
        setActivitySummaryData(matrix);
      } catch (error) {
        if (!ignore) {
          setActivitiesError(error.message || 'Error cargando calendario');
        }
      } finally {
        if (!ignore) {
          setLoadingActivities(false);
        }
      }
    }
    loadCalendarActivities();
    return () => {
      ignore = true;
    };
  }, [enabled, calendarioTableId, clientDirectory, userDirectory, refreshIndex]);

  return { activitySummaryData, loadingActivities, activitiesError };
}
